import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────────────────
   INJECT FONTS + KEYFRAMES
───────────────────────────────────────────────────────── */
let _injected = false;
if (typeof document !== "undefined" && !_injected) {
  _injected = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Outfit:wght@300;400;500;600&display=swap";
  document.head.appendChild(link);
  const s = document.createElement("style");
  s.innerHTML = `
    @keyframes ab-dot    { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(.4);opacity:.2} }
    @keyframes ab-modal  { from{opacity:0;transform:translateY(28px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
    @keyframes ab-back   { from{opacity:0} to{opacity:1} }
    @keyframes ab-heartb { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }

    /* scroll-reveal base states */
    .sr-left  { opacity:0; transform:translateX(-50px); }
    .sr-right { opacity:0; transform:translateX(50px); }
    .sr-up    { opacity:0; transform:translateY(44px); }
    .sr-done  { opacity:1 !important; transform:translate(0,0) !important;
                transition:opacity .75s cubic-bezier(.22,1,.36,1), transform .75s cubic-bezier(.22,1,.36,1); }

    .ab-mem:hover .ab-av img     { transform:scale(1.07); }
    .ab-mem:hover .ab-av-ov      { opacity:1 !important; }
    .ab-mem:hover .ab-mname      { color:#CC0000 !important; }
    .ab-mem:hover                { border-color:#E63333 !important;
                                   box-shadow:0 0 0 1px rgba(230,51,51,.08),
                                              0 12px 32px rgba(26,0,0,.1) !important; }

    @media(max-width:880px){
      .ab-story-row  { flex-direction:column !important; align-items:flex-start !important; }
      .ab-step-side  { width:100% !important; max-width:100% !important; padding-left:0 !important; padding-right:0 !important; }
      .ab-step-spine { left:22px !important; }
      .ab-step-bubble{ left:0 !important; transform:none !important; margin-left:-1px !important; }
      .ab-mv-grid    { grid-template-columns:1fr !important; }
      .ab-modal-wrap { grid-template-columns:1fr !important; }
    }
    @media(max-width:560px){
      .ab-members-grid { grid-template-columns:repeat(2,1fr) !important; }
    }
  `;
  document.head.appendChild(s);
}

/* ─────────────────────────────────────────────────────────
   TOKENS  — 100% light ADBA palette
───────────────────────────────────────────────────────── */
const C = {
  r3: "#CC0000", r4: "#E63333", r5: "#FF6666",
  rg: "linear-gradient(135deg,#7B0000,#CC0000)",
  w: "#FFFFFF", off: "#FFF8F8", off2: "#FFF2F2", border: "#F0D8D8",
  text: "#1A0000", sub: "#5C2020", light: "#B07070",
  fd: "'Barlow Condensed',sans-serif",
  fu: "'Outfit',sans-serif",
};

/* ─────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────── */
// const STEPS = [
//   {
//     year: "2018", icon: "🏸", side: "left", num: "01", title: "The Founding",
//     desc: "A group of 5 passionate players came together in Anand with one court and one mission — give every child in the district access to quality badminton coaching.",
//     tag: "5 founders · 1 court · 1 dream"
//   },
//   {
//     year: "2019", icon: "🏆", side: "right", num: "02", title: "First Tournament",
//     desc: "Our inaugural district tournament drew 48 participants. The energy in that gym told us immediately that we were onto something far bigger than we had imagined.",
//     tag: "48 players · District debut"
//   },
//   {
//     year: "2020", icon: "📋", side: "left", num: "03", title: "GBA Affiliation",
//     desc: "ADBA earned official affiliation with Gujarat Badminton Association, unlocking state-level competition pathways and formal recognition for our players.",
//     tag: "GBA Affiliated · State access unlocked"
//   },
//   {
//     year: "2021", icon: "⭐", side: "right", num: "04", title: "State Honours",
//     desc: "Three junior players qualified for nationals. GBA recognised ADBA as the most active district association in all of Gujarat — a milestone we are deeply proud of.",
//     tag: "3 national qualifiers · GBA Award"
//   },
//   {
//     year: "2022", icon: "🏫", side: "left", num: "05", title: "Academy Launch",
//     desc: "The ADBA Academy launched structured U13, U15 and U17 batches with BWF-certified coaches — bringing professional coaching to 200+ young athletes.",
//     tag: "200+ students · BWF certified"
//   },
//   {
//     year: "2024", icon: "🚀", side: "right", num: "06", title: "Growing Strong",
//     desc: "With 500+ registered players, 12+ annual events and 120+ Patreon supporters, ADBA is now the heartbeat of badminton across every corner of Anand district.",
//     tag: "500+ players · 120+ supporters"
//   },
// ];

const STEPS = [
  {
    year: "2014–2015",
    icon: "🌱",
    side: "left",
    num: "01",
    title: "Background & Foundation",
    desc: (
      <>
        Before ADBA was established, Anand district had no formal badminton
        governing body, making it difficult for players to access state-level
        tournaments and structured development opportunities. Recognizing this
        gap, <strong className="name-highlight">Avinash Desai</strong>,{" "}
        <strong className="name-highlight"> Gaurav Bhatt</strong>, and{" "}
        <strong className="name-highlight">Parthsarthi Bagchi</strong> came together with the guidance of{" "}
        <strong className="name-highlight">Mayur Parikh</strong> to build a proper badminton ecosystem for
        Anand district.
      </>
    ),
    tag: "Vision Started · Foundation Built",
  },

  {
    year: "2015",
    icon: "🏸",
    side: "right",
    num: "02",
    title: "Foundation of ADBA",
    desc: (
      <>
        Anand District Badminton Association (ADBA) was officially formed and
        registered under the leadership of{" "}
        <strong className="name-highlight">Avinash Desai</strong> with the guidance of{" "}
        <strong className="name-highlight"> Mayur Parikh</strong>, creating the first formal badminton
        structure for Anand district.
      </>
    ),
    tag: "Official Registration · GBA Affiliation",
  },
  {
    year: "2016–2018",
    icon: "📈",
    side: "left",
    num: "03",
    title: "Grassroots Development",
    desc: "ADBA focused on strengthening badminton at the grassroots level by organizing multiple district tournaments, improving tournament communication, and increasing participation in state competitions.",
    tag: "District Growth · Player Development"
  },
  {
    year: "2019",
    icon: "🏆",
    side: "right",
    num: "04",
    title: "First Open Anand Tournament",
    desc: "ADBA organized its first Open Anand Tournament featuring state-ranked players, giving local athletes the opportunity to compete against top-level talent and raising the district’s competitive standards.",
    tag: "State Players · Major Milestone"
  },
  {
    year: "2021",
    icon: "⭐",
    side: "left",
    num: "05",
    title: "First State Championship",
    desc: "ADBA hosted its first-ever U-17 & U-19 State Championship with live scoring, professional event execution, and enhanced spectator arrangements, earning recognition as one of Gujarat’s best-organized tournaments.",
    tag: "Live Scoring · State-Level Recognition"
  },
  {
    year: "2022",
    icon: "🎯",
    side: "right",
    num: "06",
    title: "State Selection Tournaments",
    desc: "ADBA hosted 8 days of back-to-back U-19 and Senior state selection tournaments for West Zone and National Championship selections, featuring top players from across Gujarat.",
    tag: "8-Day Festival · Elite Competition"
  },
  {
    year: "2023",
    icon: "🥈",
    side: "left",
    num: "07",
    title: "West Zone Success",
    desc: <>Anand district players earned significant representation in the Gujarat team, while <strong className="name-highlight"> Gaurav Bhatt</strong>{" "} served as Team Manager as Gujarat secured the Silver Medal in the Senior Mixed Team Event at the West Zone Championship."</>,
    tag: "Silver Medal · Gujarat Representation"
  },
  {
    year: "2024",
    icon: "🚀",
    side: "right",
    num: "08",
    title: "West Zone Interstate Championship",
    desc: "ADBA successfully hosted the prestigious West Zone Interstate Badminton Championship with world-class facilities, live scoring, VIP lounges, and participation from multiple states. Gujarat Women’s Team achieved a historic Gold Medal victory.",
    tag: "Historic Gold · National-Level Hosting"
  },
];
const MEMBERS = [
  {
    name: "Dr. Hemant Antani", role: "President", seed: "Arvind", bg: "b6e3f4", joined: "2018",
    tagline: "Founder & driving force behind ADBA's vision",
    quote: "Badminton is not just a sport here — it's a way of life for the people of Anand.",
    stats: [{ l: "Years", v: "6+" }, { l: "Tournaments", v: "12+" }, { l: "Players", v: "500+" }],
    history: ["Founded ADBA in 2018 with 4 fellow enthusiasts and a single court.", "Former state-level GBA player 2005–2014 with 3 state medals.", "Secured GBA affiliation in 2020 through persistent advocacy.", "Organised the first West Zone camp ever hosted in Anand district.", "Elected President for 3 consecutive terms — 2018, 2020, and 2022."],
    achievements: [{ icon: "🏅", text: "GBA Best Association Award 2021" }, { icon: "🏆", text: "12+ tournaments organised" }, { icon: "👥", text: "500+ players enrolled" }]
  },
  {
    name: "Shri Avinash Desai", role: "Vice President", seed: "Sunita", bg: "ffdfbf", joined: "2018",
    tagline: "The organisational backbone of every ADBA event",
    quote: "Organisation is the backbone of every successful championship and every successful life.",
    stats: [{ l: "Schools", v: "8" }, { l: "Students", v: "200+" }, { l: "Years", v: "6+" }],
    history: ["Co-founded ADBA and managed all administrative work from day one.", "Introduced online tournament registration in 2020.", "Spearheaded BWF coach certification drive training 5 coaches.", "Maintains active MOUs with 8 schools across Anand district.", "Digitised all ADBA tournament records and created the digital archive."],
    achievements: [{ icon: "🏫", text: "8 school partnership MOUs" }, { icon: "💻", text: "Full digital transformation" }, { icon: "📋", text: "BWF certification drive led" }]
  },
  {
    name: "Shir Ramesh Pathak", role: "Vice President", seed: "Ramesh", bg: "c0aede", joined: "2019",
    tagline: "BWF Level-2 certified — 15 years on the coaching floor",
    quote: "Every champion was once a beginner who simply refused to give up.",
    stats: [{ l: "Trained", v: "150+" }, { l: "Nationals", v: "3" }, { l: "Exp.", v: "15yr" }],
    history: ["BWF Level-2 certified coach with 15 years of active experience.", "Joined ADBA in 2019 as the first full-time professional coach.", "Trained 3 players who represented Gujarat at nationals.", "Introduced video analysis sessions for U17 — a district first.", "Created ADBA's complete coaching curriculum for all age groups."],
    achievements: [{ icon: "🎖️", text: "3 national-level players produced" }, { icon: "📹", text: "Video analysis program launched" }, { icon: "📚", text: "ADBA coaching curriculum authored" }]
  },
  {
    name: "Shir Gaurav Bhatt", role: "Hon. Secretary", seed: "Priya2", bg: "d1fae5", joined: "2019",
    tagline: "Keeping ADBA financially sound and fully transparent",
    quote: "Every rupee raised goes directly back into the hands of our players.",
    stats: [{ l: "Raised", v: "₹4L+" }, { l: "Patrons", v: "120+" }, { l: "Years", v: "5+" }],
    history: ["Manages all ADBA accounts, sponsorships and Patreon funds.", "Secured ₹4 lakh in sponsorship for the 2023 annual tournament.", "Introduced transparent public financial reporting in 2021.", "Launched the Patreon supporter program now at 120+ monthly patrons.", "Secured a government sports development grant for court infrastructure."],
    achievements: [{ icon: "💰", text: "₹4L+ sponsorship secured" }, { icon: "📊", text: "Transparent public audit system" }, { icon: "❤️", text: "Patreon program launched" }]
  },
  {
    name: "Shir Rahul Patel", role: "Hon. Jt. Secretary", seed: "Kiran2", bg: "fecaca", joined: "2021",
    tagline: "U13 & U15 specialist with a gift for inspiring young players",
    quote: "The joy on a child's face after their first winning rally — that is why I coach.",
    stats: [{ l: "Students", v: "80+" }, { l: "Titles", v: "2" }, { l: "Years", v: "3+" }],
    history: ["Former U17 state silver medallist (2014) from Anand district.", "Joined ADBA Academy as junior coach in 2021.", "Coaches 80+ junior students across two daily training sessions.", "Introduced drills that improved player retention by 40%.", "Received GBA Best Coach Award 2023."],
    achievements: [{ icon: "🥈", text: "Former U17 State Silver Medallist" }, { icon: "🏅", text: "GBA Best Coach Award 2023" }, { icon: "📈", text: "40% improvement in retention" }]
  },
  {
    name: "Dr. Chirayu Shastri", role: "Hon. Jt. Secretary", seed: "Deepak", bg: "ede9fe", joined: "2020",
    tagline: "The man who turns vision into memorable tournaments",
    quote: "A well-run tournament is itself a tribute to the sport and to the people who love it.",
    stats: [{ l: "Events", v: "20+" }, { l: "Participants", v: "300+" }, { l: "Followers", v: "5K+" }],
    history: ["Organises all ADBA tournaments, camps and celebration events.", "Managed West Zone tournament (2022) with 300+ participants.", "Built ADBA's social media presence to 5,000+ followers.", "Introduced live scoring boards at all major events from 2021.", "Created the ADBA annual events calendar."],
    achievements: [{ icon: "🎯", text: "West Zone 2022 — 300+ participants" }, { icon: "📱", text: "5K+ social media community" }, { icon: "📺", text: "Live scoring system deployed" }]
  },
  {
    name: "Shri Neal Patel", role: "Treasurer", seed: "Neha", bg: "fef3c7", joined: "2020",
    tagline: "BAI-certified umpire and guardian of fair play",
    quote: "Fair play begins long before the first serve — it begins with a fair official.",
    stats: [{ l: "Trained", v: "20+" }, { l: "State Events", v: "6" }, { l: "Years", v: "4+" }],
    history: ["BAI-certified umpire with 8 years of officiating experience.", "Trains and certifies local umpires under ADBA's program.", "Officiated at 6 GBA state-level tournaments.", "Authored ADBA's official rules guide and umpire training manual.", "Set up ADBA's first structured umpire certification process."],
    achievements: [{ icon: "⚖️", text: "BAI Certified Umpire" }, { icon: "📖", text: "Official ADBA rulebook authored" }, { icon: "🎓", text: "20+ local umpires certified" }]
  },
  // {
  //   name: "Amit Kumar", role: "Player Representative", seed: "Amit2", bg: "b6e3f4", joined: "2022",
  //   tagline: "Current district champion and voice of the players",
  //   quote: "Representing Anand on court — wearing this district's name — is the greatest honour I know.",
  //   stats: [{ l: "Titles", v: "2" }, { l: "Ranking", v: "Top 10" }, { l: "Mentees", v: "15+" }],
  //   history: ["Current district men's singles champion — 2022 and 2023.", "Represents players on the ADBA executive committee.", "Participated in the BAI national ranking tournament 2023.", "Voluntarily mentors 15+ U17 players outside coaching hours.", "Advocates for better facilities and equipment funding."],
  //   achievements: [{ icon: "🏅", text: "District Champion 2022 & 2023" }, { icon: "🏸", text: "BAI National Ranking Participant" }, { icon: "🤝", text: "15+ voluntary mentees" }]
  // },
];

/* ─────────────────────────────────────────────────────────
   SCROLL-REVEAL HOOK
───────────────────────────────────────────────────────── */
const useSR = (cls = "sr-up", delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.classList.add(cls);
    if (delay) el.style.transitionDelay = delay + "s";
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("sr-done"); obs.disconnect(); }
    }, { threshold: 0.12, rootMargin: "0px 0px -36px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [cls, delay]);
  return ref;
};

/* simple visibility hook */
const useVis = (threshold = 0.08) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.disconnect(); }
    }, { threshold, rootMargin: "0px 0px -24px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
};

/* ─────────────────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────────────────── */
const SHead = ({ words, sub }) => (
  <div style={{ marginBottom: 44 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
      <div style={{ width: 4, height: 36, background: C.rg, borderRadius: 2, flexShrink: 0 }} />
      <h2 style={{
        fontFamily: C.fd, fontSize: "clamp(1.6rem,3.5vw,2.5rem)", fontWeight: 800,
        letterSpacing: 1, textTransform: "uppercase", color: C.text, margin: 0, lineHeight: 1
      }}>
        {words.map((w, i) => (
          <span key={i} style={{ color: i === words.length - 1 ? C.r3 : C.text }}>{w}{" "}</span>
        ))}
      </h2>
    </div>
    {sub && <p style={{
      fontFamily: C.fu, fontSize: "0.88rem", color: C.sub,
      lineHeight: 1.75, maxWidth: 480, marginLeft: 18, fontWeight: 400, marginTop: 0
    }}>{sub}</p>}
  </div>
);

/* ─────────────────────────────────────────────────────────
   STORY STEP CARD  (content box, left or right)
───────────────────────────────────────────────────────── */
const StepCard = ({ s, delay }) => {
  const ref = useSR(s.side === "left" ? "sr-left" : "sr-right", delay);
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: C.w,
        border: `1px solid ${hov ? C.r4 : C.border}`,
        borderRadius: 16,
        padding: "22px 22px 20px",
        boxShadow: hov
          ? "0 0 0 1px rgba(230,51,51,.1),0 18px 44px rgba(26,0,0,.1)"
          : "0 2px 12px rgba(26,0,0,.06)",
        transition: "border-color .28s,box-shadow .28s",
        position: "relative", overflow: "hidden",
        cursor: "default",
        maxWidth: 900, width: "100%",
      }}
    >
      {/* top red accent on hover */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: C.rg, opacity: hov ? 1 : 0, transition: "opacity .28s"
      }} />

      {/* large faded step number */}
      <div style={{
        position: "absolute", bottom: 6, right: 12,
        fontFamily: C.fd, fontSize: "4.5rem", fontWeight: 800,
        color: C.off2, lineHeight: 1, userSelect: "none", letterSpacing: -2,
        transition: "color .28s", pointerEvents: "none"
      }}>{s.num}</div>

      {/* year + icon row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: hov ? C.rg : C.off2, border: `1px solid ${hov ? C.r4 : C.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.25rem", flexShrink: 0, transition: "all .28s",
          boxShadow: hov ? "0 4px 12px rgba(123,0,0,.25)" : "none"
        }}>
          {s.icon}
        </div>
        <span style={{
          fontFamily: C.fd, fontSize: "0.80rem", fontWeight: 800,
          letterSpacing: "2px", textTransform: "uppercase",
          color: hov ? C.r3 : C.light, transition: "color .28s"
        }}>{s.year}</span>
      </div>

      <div style={{
        fontFamily: C.fd, fontSize: "1.20rem", fontWeight: 800,
        textTransform: "uppercase", letterSpacing: .5, color: C.text,
        marginBottom: 8, lineHeight: 1
      }}>{s.title}</div>

      <p style={{
        fontFamily: C.fu, fontSize: "0.90rem", color: C.sub,
        lineHeight: 1.72, fontWeight: 300, margin: "0 0 14px",
        position: "relative", zIndex: 1
      }}>{s.desc}</p>

      {/* tag pill */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontFamily: C.fu, fontSize: "0.6rem", fontWeight: 600,
        letterSpacing: "1px", textTransform: "uppercase",
        color: C.r3, background: C.off2, border: `1px solid ${C.border}`,
        padding: "3px 11px", borderRadius: 100, position: "relative", zIndex: 1
      }}>
        <span style={{ width: 4, height: 4, borderRadius: "50%", background: C.r3, flexShrink: 0 }} />
        {s.tag}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   STORY ROW  (year bubble centre + card left or right)
───────────────────────────────────────────────────────── */
const StoryRow = ({ s, idx, isLast }) => {
  const bubRef = useSR("sr-up", idx * 0.1);
  const isLeft = s.side === "left";

  return (
    <div className="ab-story-row" style={{
      display: "flex", alignItems: "center",
      position: "relative", zIndex: 1,
      marginBottom: isLast ? 0 : 48,
      gap: 0,
    }}>
      {/* LEFT half */}
      <div className="ab-step-side" style={{
        flex: 1, display: "flex",
        justifyContent: "flex-end",
        paddingRight: 36,
      }}>
        {isLeft
          ? <StepCard s={s} delay={idx * 0.08} />
          : <div />}
      </div>

      {/* CENTRE bubble */}
      <div ref={bubRef} className="ab-step-bubble" style={{
        flexShrink: 0,
        width: 64, height: 64, borderRadius: "50%",
        background: C.rg,
        border: `4px solid ${C.w}`,
        boxShadow: `0 0 0 3px ${C.r3},0 8px 24px rgba(123,0,0,.3)`,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        zIndex: 2,
        animation: "ab-heartb 3.5s ease-in-out infinite",
      }}>
        <span style={{ fontSize: "1.3rem", lineHeight: 1 }}>{s.icon}</span>
        <span style={{
          fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 800,
          color: C.w, letterSpacing: "1px", marginTop: 1
        }}>{s.year}</span>
      </div>

      {/* RIGHT half */}
      <div className="ab-step-side" style={{
        flex: 1,
        paddingLeft: 36,
      }}>
        {!isLeft
          ? <StepCard s={s} delay={idx * 0.08} />
          : <div />}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   MEMBER CARD
───────────────────────────────────────────────────────── */
const MemberCard = ({ m, delay, onClick }) => {
  const [ref, vis] = useVis();
  return (
    <div ref={ref} className="ab-mem" onClick={onClick}
      style={{
        opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(18px)",
        transition: `opacity .45s ${delay}s ease,transform .45s ${delay}s ease,border-color .22s,box-shadow .22s`,
        cursor: "pointer",
        background: C.w,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: "22px 14px 18px",
        boxShadow: "0 1px 4px rgba(26,0,0,.05)",
        display: "flex", flexDirection: "column", alignItems: "center",
      }}
    >
      {/* avatar */}
      <div className="ab-av" style={{ position: "relative", marginBottom: 12 }}>
        <div style={{
          width: 80, height: 80, borderRadius: "50%", overflow: "hidden",
          border: `2px solid ${C.border}`, transition: "border-color .22s"
        }}>
          {/* <img src={`https://api.dicebear.com/9.x/notionists/svg?seed=${m.seed}&backgroundColor=${m.bg}`}
            alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform .3s" }} /> */}
          <img src={"profile.png"}
            alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform .3s" }} />
        </div>
        {/* <div className="ab-av-ov" style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          background: "rgba(100,0,0,.5)", display: "flex", alignItems: "center",
          justifyContent: "center", opacity: 0, transition: "opacity .22s",
          fontFamily: C.fu, fontSize: "0.58rem", fontWeight: 700,
          color: C.w, letterSpacing: "1px", textTransform: "uppercase"
        }}>View</div> */}
      </div>

      <div className="ab-mname" style={{
        fontFamily: C.fd, fontSize: "0.95rem", fontWeight: 800,
        textTransform: "uppercase", letterSpacing: .2, color: C.text,
        lineHeight: 1.1, textAlign: "center", marginBottom: 4, transition: "color .22s"
      }}>{m.name}</div>
      <div style={{
        fontFamily: C.fu, fontSize: "0.6rem", fontWeight: 600,
        letterSpacing: "1.2px", textTransform: "uppercase",
        color: C.light, textAlign: "center"
      }}>{m.role}</div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   MEMBER MODAL  — clean red / white
───────────────────────────────────────────────────────── */
// const MemberModal = ({ m, onClose }) => {
//   useEffect(() => {
//     const fn = e => { if (e.key === "Escape") onClose(); };
//     window.addEventListener("keydown", fn);
//     document.body.style.overflow = "hidden";
//     return () => { window.removeEventListener("keydown", fn); document.body.style.overflow = ""; };
//   }, [onClose]);

//   return (
//     <div onClick={onClose} style={{
//       position: "fixed", inset: 0, zIndex: 2000,
//       background: "rgba(26,0,0,.45)", backdropFilter: "blur(8px)",
//       display: "flex", alignItems: "center", justifyContent: "center",
//       padding: "16px", animation: "ab-back .2s ease"
//     }}>

//       <div className="ab-modal-wrap" onClick={e => e.stopPropagation()} style={{
//         display: "grid",
//         // Remove gridTemplateColumns from here so the CSS injected classes can control it safely!
//         background: C.w,
//         borderRadius: 20,
//         maxWidth: 780,
//         width: "100%",
//         // CHANGE: Allow the modal wrapper to scroll its whole frame on mobile if stacked
//         maxHeight: "90vh",
//         overflowY: "auto",
//         boxShadow: "0 32px 80px rgba(26,0,0,.22)",
//         animation: "ab-modal .3s ease",
//       }}>

//         {/* LEFT  — red */}
//         <div style={{
//           background: C.rg, padding: "36px 22px",
//           display: "flex", flexDirection: "column", alignItems: "center",
//           position: "relative", overflow: "hidden"
//         }}>
//           {/* rings */}
//           <div style={{
//             position: "absolute", width: 260, height: 260, borderRadius: "50%",
//             border: "1px solid rgba(255,255,255,.08)", top: "38%", left: "50%",
//             transform: "translate(-50%,-50%)"
//           }} />
//           <div style={{
//             position: "absolute", width: 170, height: 170, borderRadius: "50%",
//             border: "1px solid rgba(255,255,255,.11)", top: "38%", left: "50%",
//             transform: "translate(-50%,-50%)"
//           }} />

//           {/* avatar */}
//           <div style={{
//             width: 100, height: 100, borderRadius: "50%",
//             border: "3px solid rgba(255,255,255,.45)", overflow: "hidden",
//             boxShadow: "0 0 0 6px rgba(255,255,255,.12),0 12px 32px rgba(0,0,0,.2)",
//             marginBottom: 14, position: "relative", zIndex: 1, flexShrink: 0
//           }}>
//             <img src={`https://api.dicebear.com/9.x/notionists/svg?seed=${m.seed}&backgroundColor=${m.bg}`}
//               alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
//           </div>

//           <div style={{ textAlign: "center", marginBottom: 12, position: "relative", zIndex: 1 }}>
//             <div style={{
//               fontFamily: C.fd, fontSize: "1.25rem", fontWeight: 800,
//               textTransform: "uppercase", color: C.w, letterSpacing: .5, lineHeight: 1.05
//             }}>{m.name}</div>
//             <div style={{
//               fontFamily: C.fu, fontSize: "0.6rem", fontWeight: 600,
//               color: "rgba(255,255,255,.65)", letterSpacing: "1.5px",
//               textTransform: "uppercase", marginTop: 5
//             }}>{m.role}</div>
//           </div>

//           <p style={{
//             fontFamily: C.fu, fontSize: "0.76rem", color: "rgba(255,255,255,.55)",
//             lineHeight: 1.65, textAlign: "center", fontWeight: 300, marginBottom: 16,
//             position: "relative", zIndex: 1
//           }}>{m.tagline}</p>

//           {/* stats */}
//           <div style={{
//             display: "grid", gridTemplateColumns: "repeat(3,1fr)",
//             gap: 2, width: "100%", marginBottom: 16, borderRadius: 10,
//             overflow: "hidden", position: "relative", zIndex: 1
//           }}>
//             {m.stats.map((st, i) => (
//               <div key={i} style={{
//                 padding: "11px 5px", textAlign: "center",
//                 background: "rgba(0,0,0,.18)"
//               }}>
//                 <div style={{
//                   fontFamily: C.fd, fontSize: "1.3rem", fontWeight: 800,
//                   color: C.w, letterSpacing: -1, lineHeight: 1
//                 }}>{st.v}</div>
//                 <div style={{
//                   fontFamily: C.fu, fontSize: "0.5rem", fontWeight: 600,
//                   letterSpacing: "1px", textTransform: "uppercase",
//                   color: "rgba(255,255,255,.45)", marginTop: 3
//                 }}>{st.l}</div>
//               </div>
//             ))}
//           </div>

//           {/* since */}
//           <div style={{
//             display: "flex", alignItems: "center", gap: 6,
//             padding: "7px 12px", borderRadius: 8,
//             background: "rgba(0,0,0,.15)", border: "1px solid rgba(255,255,255,.12)",
//             width: "100%", justifyContent: "center",
//             position: "relative", zIndex: 1, marginBottom: "auto"
//           }}>
//             <span style={{ fontSize: "0.75rem" }}>📅</span>
//             <span style={{
//               fontFamily: C.fu, fontSize: "0.68rem", fontWeight: 600,
//               color: "rgba(255,255,255,.6)"
//             }}>
//               Member since <strong style={{ color: C.w }}>{m.joined}</strong>
//             </span>
//           </div>

//           {/* quote */}
//           <div style={{
//             paddingTop: 14, borderTop: "1px solid rgba(255,255,255,.1)",
//             width: "100%", position: "relative", zIndex: 1, marginTop: 14
//           }}>
//             <div style={{
//               fontFamily: C.fd, fontSize: "2.2rem", lineHeight: .5,
//               color: "rgba(255,255,255,.22)", marginBottom: 8
//             }}>"</div>
//             <p style={{
//               fontFamily: C.fu, fontSize: "0.76rem",
//               color: "rgba(255,255,255,.5)", lineHeight: 1.65,
//               fontStyle: "italic", fontWeight: 300, margin: 0
//             }}>{m.quote}</p>
//           </div>
//         </div>

//         {/* RIGHT  — white scrollable */}
//         <div style={{
//           overflowY: "auto", padding: "26px 26px",
//           display: "flex", flexDirection: "column", gap: 22
//         }}>

//           {/* header */}
//           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
//             <div>
//               <div style={{
//                 fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 700,
//                 letterSpacing: "2.5px", textTransform: "uppercase", color: C.r3, marginBottom: 3
//               }}>Member Profile</div>
//               <div style={{
//                 fontFamily: C.fd, fontSize: "1.45rem", fontWeight: 800,
//                 textTransform: "uppercase", color: C.text, letterSpacing: .5, lineHeight: 1
//               }}>{m.name}</div>
//             </div>
//             <button onClick={onClose} style={{
//               width: 32, height: 32, borderRadius: "50%",
//               background: C.off2, border: `1px solid ${C.border}`, color: C.light,
//               cursor: "pointer", fontSize: "0.85rem", flexShrink: 0,
//               display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s"
//             }}
//               onMouseEnter={e => { e.currentTarget.style.background = C.r4; e.currentTarget.style.color = C.w; }}
//               onMouseLeave={e => { e.currentTarget.style.background = C.off2; e.currentTarget.style.color = C.light; }}>✕</button>
//           </div>

//           {/* journey */}
//           <div>
//             <div style={{
//               fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 700,
//               letterSpacing: "2.5px", textTransform: "uppercase", color: C.r3,
//               marginBottom: 14, display: "flex", alignItems: "center", gap: 10
//             }}>
//               <span>Journey</span>
//               <div style={{ flex: 1, height: 1, background: C.border }} />
//             </div>
//             {m.history.map((h, i) => (
//               <div key={i} style={{ display: "flex", gap: 0, alignItems: "stretch" }}>
//                 <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 22, flexShrink: 0 }}>
//                   <div style={{
//                     width: 9, height: 9, borderRadius: "50%", background: C.rg,
//                     flexShrink: 0, marginTop: 5, border: `2px solid ${C.w}`,
//                     boxShadow: `0 0 0 2px ${C.r3}`
//                   }} />
//                   {i < m.history.length - 1 && <div style={{
//                     width: 1, flex: 1,
//                     background: C.border, minHeight: 14, margin: "2px 0"
//                   }} />}
//                 </div>
//                 <div style={{ paddingBottom: i < m.history.length - 1 ? 13 : 0, paddingLeft: 10 }}>
//                   <p style={{
//                     fontFamily: C.fu, fontSize: "0.83rem", color: C.sub,
//                     lineHeight: 1.65, margin: 0, fontWeight: 400
//                   }}>{h}</p>
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* achievements */}
//           <div>
//             <div style={{
//               fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 700,
//               letterSpacing: "2.5px", textTransform: "uppercase", color: C.r3,
//               marginBottom: 14, display: "flex", alignItems: "center", gap: 10
//             }}>
//               <span>Key Achievements</span>
//               <div style={{ flex: 1, height: 1, background: C.border }} />
//             </div>
//             {m.achievements.map((a, i) => (
//               <div key={i} style={{
//                 display: "flex", gap: 11, alignItems: "center",
//                 background: C.off, border: `1px solid ${C.border}`,
//                 borderRadius: 10, padding: "10px 14px", marginBottom: 8
//               }}>
//                 <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>{a.icon}</span>
//                 <span style={{ fontFamily: C.fu, fontSize: "0.82rem", color: C.sub, fontWeight: 500 }}>{a.text}</span>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

const MemberModal = ({ m, onClose }) => {
  // Track screen width to dynamically toggle your exact inline style configurations
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 880 : false
  );

  useEffect(() => {
    // Escape key listener to close modal
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    document.body.style.overflow = "hidden";

    // Handle screen resize to switch styles smoothly
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 880);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("keydown", fn);
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(26,0,0,.45)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "16px", animation: "ab-back .2s ease"
    }}>

      <div
        className="ab-modal-wrap"
        onClick={e => e.stopPropagation()}
        style={isSmallScreen ? {
          /* 📱 SMALL SCREEN STYLE */
          display: "grid",
          background: C.w,
          borderRadius: 20,
          maxWidth: 780,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 32px 80px rgba(26,0,0,.22)",
          animation: "ab-modal .3s ease",
        } : {
          /* 💻 BIG SCREEN STYLE */
          display: "grid",
          gridTemplateColumns: "240px 1fr",
          background: C.w,
          borderRadius: 20,
          maxWidth: 780,
          width: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(26,0,0,.22)",
          animation: "ab-modal .3s ease",
        }}
      >

        {/* LEFT — red panel */}
        <div style={{
          background: C.rg, padding: "36px 22px",
          display: "flex", flexDirection: "column", alignItems: "center",
          position: "relative", overflow: "hidden"
        }}>
          {/* rings */}
          <div style={{
            position: "absolute", width: 260, height: 260, borderRadius: "50%",
            border: "1px solid rgba(255,255,255,.08)", top: "38%", left: "50%",
            transform: "translate(-50%,-50%)"
          }} />
          <div style={{
            position: "absolute", width: 170, height: 170, borderRadius: "50%",
            border: "1px solid rgba(255,255,255,.11)", top: "38%", left: "50%",
            transform: "translate(-50%,-50%)"
          }} />

          {/* avatar */}
          <div style={{
            width: 100, height: 100, borderRadius: "50%",
            border: "3px solid rgba(255,255,255,.45)", overflow: "hidden",
            boxShadow: "0 0 0 6px rgba(255,255,255,.12),0 12px 32px rgba(0,0,0,.2)",
            marginBottom: 14, position: "relative", zIndex: 1, flexShrink: 0
          }}>
            <img src={`https://api.dicebear.com/9.x/notionists/svg?seed=${m.seed}&backgroundColor=${m.bg}`}
              alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>

          <div style={{ textAlign: "center", marginBottom: 12, position: "relative", zIndex: 1 }}>
            <div style={{
              fontFamily: C.fd, fontSize: "1.25rem", fontWeight: 800,
              textTransform: "uppercase", color: C.w, letterSpacing: .5, lineHeight: 1.05
            }}>{m.name}</div>
            <div style={{
              fontFamily: C.fu, fontSize: "0.6rem", fontWeight: 600,
              color: "rgba(255,255,255,.65)", letterSpacing: "1.5px",
              textTransform: "uppercase", marginTop: 5
            }}>{m.role}</div>
          </div>

          <p style={{
            fontFamily: C.fu, fontSize: "0.76rem", color: "rgba(255,255,255,.55)",
            lineHeight: 1.65, textAlign: "center", fontWeight: 300, marginBottom: 16,
            position: "relative", zIndex: 1
          }}>{m.tagline}</p>

          {/* stats */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3,1fr)",
            gap: 2, width: "100%", marginBottom: 16, borderRadius: 10,
            overflow: "hidden", position: "relative", zIndex: 1
          }}>
            {m.stats.map((st, i) => (
              <div key={i} style={{
                padding: "11px 5px", textAlign: "center",
                background: "rgba(0,0,0,.18)"
              }}>
                <div style={{
                  fontFamily: C.fd, fontSize: "1.3rem", fontWeight: 800,
                  color: C.w, letterSpacing: -1, lineHeight: 1
                }}>{st.v}</div>
                <div style={{
                  fontFamily: C.fu, fontSize: "0.5rem", fontWeight: 600,
                  letterSpacing: "1px", textTransform: "uppercase",
                  color: "rgba(255,255,255,.45)", marginTop: 3
                }}>{st.l}</div>
              </div>
            ))}
          </div>

          {/* since */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "7px 12px", borderRadius: 8,
            background: "rgba(0,0,0,.15)", border: "1px solid rgba(255,255,255,.12)",
            width: "100%", justifyContent: "center",
            position: "relative", zIndex: 1, marginBottom: "auto"
          }}>
            <span style={{ fontSize: "0.75rem" }}>📅</span>
            <span style={{
              fontFamily: C.fu, fontSize: "0.68rem", fontWeight: 600,
              color: "rgba(255,255,255,.6)"
            }}>
              Member since <strong style={{ color: C.w }}>{m.joined}</strong>
            </span>
          </div>

          {/* quote */}
          <div style={{
            paddingTop: 14, borderTop: "1px solid rgba(255,255,255,.1)",
            width: "100%", position: "relative", zIndex: 1, marginTop: 14
          }}>
            <div style={{
              fontFamily: C.fd, fontSize: "2.2rem", lineHeight: .5,
              color: "rgba(255,255,255,.22)", marginBottom: 8
            }}>"</div>
            <p style={{
              fontFamily: C.fu, fontSize: "0.76rem",
              color: "rgba(255,255,255,.5)", lineHeight: 1.65,
              fontStyle: "italic", fontWeight: 300, margin: 0
            }}>{m.quote}</p>
          </div>
        </div>

        {/* RIGHT — white scrollable panel */}
        <div style={{
          // Only use internal scroll on big desktop screens to prevent double-scrollbar glitches on mobile!
          overflowY: isSmallScreen ? "visible" : "auto",
          padding: "26px 26px",
          display: "flex", flexDirection: "column", gap: 22
        }}>

          {/* header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{
                fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 700,
                letterSpacing: "2.5px", textTransform: "uppercase", color: C.r3, marginBottom: 3
              }}>Member Profile</div>
              <div style={{
                fontFamily: C.fd, fontSize: "1.45rem", fontWeight: 800,
                textTransform: "uppercase", color: C.text, letterSpacing: .5, lineHeight: 1
              }}>{m.name}</div>
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: "50%",
              background: C.off2, border: `1px solid ${C.border}`, color: C.light,
              cursor: "pointer", fontSize: "0.85rem", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s"
            }}
              onMouseEnter={e => { e.currentTarget.style.background = C.r4; e.currentTarget.style.color = C.w; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.off2; e.currentTarget.style.color = C.light; }}>✕</button>
          </div>

          {/* journey */}
          <div>
            <div style={{
              fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 700,
              letterSpacing: "2.5px", textTransform: "uppercase", color: C.r3,
              marginBottom: 14, display: "flex", alignItems: "center", gap: 10
            }}>
              <span>Journey</span>
              <div style={{ flex: 1, height: 1, background: C.border }} />
            </div>
            {m.history.map((h, i) => (
              <div key={i} style={{ display: "flex", gap: 0, alignItems: "stretch" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 22, flexShrink: 0 }}>
                  <div style={{
                    width: 9, height: 9, borderRadius: "50%", background: C.rg,
                    flexShrink: 0, marginTop: 5, border: `2px solid ${C.w}`,
                    boxShadow: `0 0 0 2px ${C.r3}`
                  }} />
                  {i < m.history.length - 1 && <div style={{
                    width: 1, flex: 1,
                    background: C.border, minHeight: 14, margin: "2px 0"
                  }} />}
                </div>
                <div style={{ paddingBottom: i < m.history.length - 1 ? 13 : 0, paddingLeft: 10 }}>
                  <p style={{
                    fontFamily: C.fu, fontSize: "0.83rem", color: C.sub,
                    lineHeight: 1.65, margin: 0, fontWeight: 400
                  }}>{h}</p>
                </div>
              </div>
            ))}
          </div>

          {/* achievements */}
          <div>
            <div style={{
              fontFamily: C.fd, fontSize: "0.58rem", fontWeight: 700,
              letterSpacing: "2.5px", textTransform: "uppercase", color: C.r3,
              marginBottom: 14, display: "flex", alignItems: "center", gap: 10
            }}>
              <span>Key Achievements</span>
              <div style={{ flex: 1, height: 1, background: C.border }} />
            </div>
            {m.achievements.map((a, i) => (
              <div key={i} style={{
                display: "flex", gap: 11, alignItems: "center",
                background: C.off, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: "10px 14px", marginBottom: 8
              }}>
                <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>{a.icon}</span>
                <span style={{ fontFamily: C.fu, fontSize: "0.82rem", color: C.sub, fontWeight: 500 }}>{a.text}</span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};


/* ─────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────── */
const AboutPage = () => {
  const [selected, setSelected] = useState(null);
  const [heroVis, setHeroVis] = useState(false);
  const [mvRef, mvVis] = useVis(0.08);
  const [membRef, membVis] = useVis(0.04);
  const [storyRef, storyVis] = useVis(0.04);

  useEffect(() => { const t = setTimeout(() => setHeroVis(true), 80); return () => clearTimeout(t); }, []);
  const ha = (d = 0) => ({
    opacity: heroVis ? 1 : 0,
    transform: heroVis ? "translateY(0)" : "translateY(18px)",
    transition: `opacity .65s ${d}s ease,transform .65s ${d}s ease`,
  });

  return (
    <div style={{ background: C.off, fontFamily: C.fu, minHeight: "100vh" }}>

      {/* ══════════════════════════════════════
          HERO — full light theme
      ══════════════════════════════════════ */}
      <section style={{
        background: C.w, padding: "96px 5% 80px",
        position: "relative", overflow: "hidden", borderBottom: `1px solid ${C.border}`
      }}>
        {/* subtle dot pattern */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "radial-gradient(circle,rgba(204,0,0,.05) 1px,transparent 1px)",
          backgroundSize: "28px 28px"
        }} />
        {/* red glow blobs */}
        <div style={{
          position: "absolute", top: -120, left: -100, width: 440, height: 440,
          borderRadius: "50%", background: "radial-gradient(circle,rgba(204,0,0,.07) 0%,transparent 65%)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: -80, right: -80, width: 340, height: 340,
          borderRadius: "50%", background: "radial-gradient(circle,rgba(204,0,0,.05) 0%,transparent 65%)",
          pointerEvents: "none"
        }} />
        {/* left accent bar */}
        <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 4, background: C.rg }} />

        {/* <div style={{ maxWidth: 1060, margin: "0 auto", position: "relative", zIndex: 1 }}> */}
        <div style={{ maxWidth: 1500, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ maxWidth: 640 }}>
            {/* eyebrow */}
            <div style={{
              ...ha(0.04), display: "inline-flex", alignItems: "center", gap: 8,
              fontFamily: C.fu, fontSize: "0.63rem", fontWeight: 600, letterSpacing: "2.5px",
              textTransform: "uppercase", color: C.r3, border: `1px solid rgba(204,0,0,.22)`,
              background: "rgba(204,0,0,.05)", padding: "4px 16px", borderRadius: 100, marginBottom: 22
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: "50%", background: C.r3,
                display: "inline-block", animation: "ab-dot 2s infinite"
              }} />
              Est. 2014 · Anand, Gujarat, India
            </div>

            <h1 style={{
              ...ha(0.12), fontFamily: C.fd,
              fontSize: "clamp(3rem,6.5vw,5.8rem)", fontWeight: 800, lineHeight: .88,
              letterSpacing: 2, textTransform: "uppercase", color: C.text, margin: "0 0 18px"
            }}>
              ABOUT<br />
              <span style={{
                background: C.rg, WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent", backgroundClip: "text"
              }}>ADBA</span>
            </h1>

            <div style={{ ...ha(0.2), width: 52, height: 3, background: C.rg, borderRadius: 2, marginBottom: 18 }} />

            <p style={{
              ...ha(0.26), fontFamily: C.fu, fontSize: "0.97rem", color: C.sub,
              fontWeight: 300, lineHeight: 1.82, maxWidth: 500, marginBottom: 38
            }}>
              The Anand District Badminton Association is dedicated to fostering talent,
              promoting sportsmanship, and building a thriving badminton community for
              every player — from first-timers to national aspirants.
            </p>

            {/* stats row */}
            <div style={{
              ...ha(0.34), display: "flex", gap: 0, flexWrap: "wrap",
              paddingTop: 24, borderTop: `1px solid ${C.border}`
            }}>
              {[{ n: "2014", l: "Founded" }, { n: "250+", l: "Players" },
              { n: "06+", l: "Events/yr" }, { n: "GBA", l: "Affiliated" }, { n: "BAI", l: "Recognised" }
              ].map((st, i, arr) => (
                <div key={i} style={{
                  paddingRight: 22, marginRight: 22, marginBottom: 10,
                  borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : "none"
                }}>
                  <span style={{
                    display: "block", fontFamily: C.fd, fontSize: "1.9rem",
                    fontWeight: 800, color: C.text, letterSpacing: -1, lineHeight: 1
                  }}>{st.n}</span>
                  <span style={{
                    display: "block", fontFamily: C.fu, fontSize: "0.58rem",
                    fontWeight: 600, letterSpacing: "1.8px", textTransform: "uppercase",
                    color: C.light, marginTop: 3
                  }}>{st.l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          OUR STORY  — light bg, scroll-animated timeline
      ══════════════════════════════════════ */}
      <section style={{ background: C.off, padding: "80px 5% 64px" }}>
        {/* <div style={{ maxWidth: 980, margin: "0 auto" }}> */}
        <div style={{ maxWidth: 1500, margin: "0 auto" }}>

          <div ref={storyRef} style={{
            opacity: storyVis ? 1 : 0, transform: storyVis ? "none" : "translateY(18px)",
            transition: "opacity .6s ease,transform .6s ease"
          }}>
            <SHead
              words={["Our", "Story"]}
              sub="Scroll through the milestones that shaped ADBA — from a single court to a district-wide movement."
            />
          </div>

          {/* timeline wrapper */}
          <div style={{ position: "relative" }}>
            {/* central spine */}
            <div className="ab-step-spine" style={{
              position: "absolute", left: "50%",
              transform: "translateX(-50%)", top: 32, bottom: 32, width: 2,
              background: `linear-gradient(to bottom,${C.r3},rgba(204,0,0,.08))`,
              zIndex: 0
            }} />

            {STEPS.map((s, i) => (
              <StoryRow key={i} s={s} idx={i} isLast={i === STEPS.length - 1} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          MISSION & VISION
      ══════════════════════════════════════ */}
      <section style={{ background: C.w, padding: "68px 5%", borderTop: `1px solid ${C.border}` }}>
        {/* <div style={{ maxWidth: 1060, margin: "0 auto" }}> */}
        <div style={{ maxWidth: 1500, margin: "0 auto" }}>
          <div ref={mvRef} style={{
            opacity: mvVis ? 1 : 0, transform: mvVis ? "none" : "translateY(18px)",
            transition: "opacity .55s ease,transform .55s ease", marginBottom: 36
          }}>
            <SHead words={["Mission", "&", "Vision"]} />
          </div>
          <div className="ab-mv-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              {
                icon: "🎯", label: "Our Mission",
                text: "To identify, nurture and develop badminton talent across Anand district and provide every player — regardless of background — a clear pathway to excellence at state and national level."
              },
              {
                icon: "🌟", label: "Our Vision",
                text: "To make Anand district synonymous with grassroots badminton excellence — producing state and national champions while building a lifelong love for the sport in every community."
              },
            ].map((item, i) => (
              <div key={i} style={{
                background: C.off, border: `1px solid ${C.border}`,
                borderRadius: 14, padding: "26px 24px", position: "relative", overflow: "hidden"
              }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: C.rg }} />
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: C.rg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.3rem", flexShrink: 0
                  }}>{item.icon}</div>
                  <div style={{
                    fontFamily: C.fd, fontSize: "1.3rem", fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: .5, color: C.text
                  }}>{item.label}</div>
                </div>
                <p style={{
                  fontFamily: C.fu, fontSize: "0.9rem", color: C.sub,
                  lineHeight: 1.8, fontWeight: 300, margin: 0
                }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          MEET THE TEAM
      ══════════════════════════════════════ */}
      <section style={{ background: C.off, padding: "72px 5% 80px", borderTop: `1px solid ${C.border}` }}>
        {/* <div style={{ maxWidth: 1060, margin: "0 auto" }}> */}
        <div style={{ maxWidth: 1500, margin: "0 auto" }}>
          <div ref={membRef} style={{
            opacity: membVis ? 1 : 0, transform: membVis ? "none" : "translateY(18px)",
            transition: "opacity .6s ease,transform .6s ease"
          }}>
            <SHead words={["Meet the", "Team"]}
              sub="Click any member card to explore their journey and achievements." />
          </div>

          <div className="ab-members-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)", gap: 16
          }}>
            {MEMBERS.map((m, i) => (
              <MemberCard key={m.name} m={m}
                delay={Math.min(i * 0.06, .35)}
                onClick={() => setSelected(m)} />
            ))}
          </div>

          <div style={{
            marginTop: 22, textAlign: "center", fontFamily: C.fu,
            fontSize: "0.74rem", color: C.light,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6
          }}>
            {/* <span>👆</span> Click any member card to see their full profile */}
          </div>
        </div>
      </section>

      {/* MODAL */}
      {/* {selected && <MemberModal m={selected} onClose={() => setSelected(null)} />} */}
    </div>
  );
};

export default AboutPage;